import { Button, Space, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import Page from "../../Common/Page";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteDivision, useFetchDivisions } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";

const PermissionName = PermissionNames.DIVISION;

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link
              to={`/${customerId}/admin/${sport}/${organizationId}/conferences/${record.conferenceId}/divisions/manage/${record.divisionId}/`}
            >
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const DivisionsList = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, conferenceId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const [data, setData] = useState([]);
  const [conference, setConference] = useState({});
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const { data: divisionsRes, isLoading } = useFetchDivisions({
    limit: pageSize,
    offset,
    include: "conferences",
  });

  const nextLink = useMemo(() => divisionsRes?.links?.next, [divisionsRes?.links?.next]);

  const { mutate: deleteFn, isPending: isDeletePending } = useDeleteDivision();

  useEffect(() => {
    if (divisionsRes) {
      setData(divisionsRes?.data);
      if (divisionsRes?.includes?.resources?.conferences) {
        setConference(divisionsRes.includes.resources.conferences?.[conferenceId]);
      }
    }
  }, [conferenceId, divisionsRes]);

  useEffect(() => {
    setFilteredData(data);
  }, [data, setFilteredData]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected.divisionId);
  }, [deleteFn, selected.divisionId]);

  const title = useMemo(() => {
    return `${formatMessage(
      {
        id: "pageTitles.organization.divisions",
        defaultMessage: "{conferenceName} Divisions",
      },
      { conferenceName: conference?.nameLocal },
    )}`;
  }, [formatMessage, conference?.nameLocal]);

  return (
    <Page title={title} tabTitle={title}>
      <div className={"mb-2"}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className={"mb-2"}
        >
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <ConfirmationModal
              title="Delete Division"
              setIsModalOpen={setShowConfirmation}
              isModalOpen={showConfirmation}
              handleOk={handleDelete}
              handleCancel={() => {
                setSelected({});
              }}
              okButtonProps={{ danger: true }}
              okText="Delete"
            >
              <div>{`You are about to delete ${selected.nameLocal}. Is this OK?`}</div>
            </ConfirmationModal>
          </PermissionCheck>
          <strong>Divisions</strong>
          <Space>
            <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/${conferenceId}/divisions/manage`}>
                <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                  Division
                </Button>
              </Link>
            </PermissionCheck>
          </Space>
        </div>
        <Table
          loading={isLoading || isDeletePending}
          size={"small"}
          columns={columnsone({
            customerId,
            sport,
            organizationId,
            formatMessage,
            handleOpenConfirmation,
          })}
          rowKey={(record) => record?.seasonId}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions,
            showQuickJumper: false,
            total: nextLink ? offset + data.length + 1 : data.length + offset,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            showTotal: (total, range) => (
              <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
            ),
          }}
          dataSource={filteredData}
        />
      </div>
    </Page>
  );
};

export default DivisionsList;
